import { buildFontStack, CUSTOM_FONT_FAMILY } from '@proj-airi/stage-ui/constants'
import { useSettings } from '@proj-airi/stage-ui/stores/settings'
import { computed, watch } from 'vue'

const CUSTOM_FONT_STYLE_ID = 'custom-font-style'
const CUSTOM_FONT_LOAD_TIMEOUT_MS = 10_000

// 代码类元素不参与字体覆盖
const BODY_SELECTOR = `body, body *:not(code):not(pre):not(kbd):not(samp):not(.font-mono):not([class*="font-mono"])`

// ⚠️ 模块级单例状态：useCustomFont() 全局只应被调用一次（当前在 App.vue 调用）。
//    所有状态变更都必须经由 rebuildStyle() 这个唯一出口写回 <style>，避免多份样式打架。
let activeFontFace: FontFace | null = null
let fontApplyVersion = 0
// 当前是否已成功加载用户自定义字体（决定是否在字体栈最前面加 custom-user-font）
let customFontLoaded = false
// 当前「字体跟随系统」开关状态（决定回退栈用系统优先还是小赖优先）
let currentFollowSystem = false
// 无 FontFace API 的浏览器使用的 @font-face 声明（兜底路径）
let fallbackFontFaceCss: string | null = null

function cssString(value: string): string {
  return JSON.stringify(value)
}

function removeCustomFontStyle() {
  document.getElementById(CUSTOM_FONT_STYLE_ID)?.remove()
}

function removeActiveFontFace() {
  if (activeFontFace) {
    document.fonts.delete(activeFontFace)
    activeFontFace = null
  }
}

function ensureStyleTag(): HTMLStyleElement {
  let styleTag = document.getElementById(CUSTOM_FONT_STYLE_ID) as HTMLStyleElement | null
  if (!styleTag) {
    styleTag = document.createElement('style')
    styleTag.id = CUSTOM_FONT_STYLE_ID
    document.head.appendChild(styleTag)
  }
  return styleTag
}

function buildBodyRule(): string {
  const stack = buildFontStack(currentFollowSystem, customFontLoaded)
  return `${BODY_SELECTOR} {\n  font-family: ${stack} !important;\n}`
}

/**
 * 单一出口：根据 customFontLoaded / currentFollowSystem 重建注入样式。
 * 默认态（无自定义字体且不跟随系统）无需注入，交给 uno.config 默认栈（小赖优先）。
 */
function rebuildStyle() {
  if (!customFontLoaded && !currentFollowSystem && !fallbackFontFaceCss) {
    removeCustomFontStyle()
    return
  }
  const styleTag = ensureStyleTag()
  styleTag.textContent = `${fallbackFontFaceCss ? `${fallbackFontFaceCss}\n` : ''}${buildBodyRule()}\n`
}

function removeCustomFont() {
  customFontLoaded = false
  fallbackFontFaceCss = null
  removeActiveFontFace()
  rebuildStyle()
}

async function loadFontFaceWithTimeout(fontFace: FontFace): Promise<FontFace> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined
  try {
    return await Promise.race([
      fontFace.load(),
      new Promise<FontFace>((_, reject) => {
        timeoutId = setTimeout(() => reject(new Error('Font load timeout')), CUSTOM_FONT_LOAD_TIMEOUT_MS)
      }),
    ])
  }
  finally {
    if (timeoutId)
      clearTimeout(timeoutId)
  }
}

async function applyCustomFont(fontUrl?: string) {
  const applyVersion = ++fontApplyVersion

  if (!fontUrl) {
    removeCustomFont()
    return true
  }

  // 不支持 FontFace API 的浏览器：直接用 @font-face 声明兜底
  if (!('FontFace' in window)) {
    removeActiveFontFace()
    fallbackFontFaceCss = `@font-face {\n  font-family: '${CUSTOM_FONT_FAMILY}';\n  src: url(${cssString(fontUrl)});\n  font-display: swap;\n}`
    customFontLoaded = true
    rebuildStyle()
    return true
  }

  try {
    const fontFace = new FontFace(CUSTOM_FONT_FAMILY, `url(${cssString(fontUrl)})`)
    await loadFontFaceWithTimeout(fontFace)

    if (applyVersion !== fontApplyVersion)
      return true

    removeActiveFontFace()
    activeFontFace = fontFace
    document.fonts.add(fontFace)
    fallbackFontFaceCss = null
    customFontLoaded = true
    rebuildStyle()
    return true
  }
  catch (error) {
    if (applyVersion === fontApplyVersion)
      removeCustomFont()
    console.warn('[Custom Font] Browser rejected custom font:', error)
    return false
  }
}

export function useCustomFont() {
  const settings = useSettings()

  const activeFontUrl = computed(() => {
    if (settings.customFontDataUrl) {
      return settings.customFontDataUrl
    }
    return settings.customFontUrl
  })

  // 顺序说明：下面两个 watch 都是 immediate。fontFollowSystem 的回调是同步的，
  // 会在 activeFontUrl 回调里的 await（异步加载字体）真正完成前先把 currentFollowSystem 设好，
  // 因此 applyCustomFont() 最终 rebuildStyle() 时读到的开关状态已正确。
  // rebuildStyle() 是读取两个模块标志的幂等出口，任一 watch 重复触发都只会重算同一份样式，不会丢失。
  watch(activeFontUrl, async (fontUrl) => {
    const applied = await applyCustomFont(fontUrl)
    if (!applied && fontUrl && fontUrl === settings.customFontDataUrl)
      void settings.setCustomFont(undefined)
  }, { immediate: true })

  // 「字体跟随系统」开关：切换回退栈方向（系统优先 / 小赖优先）
  watch(() => settings.fontFollowSystem, (followSystem) => {
    currentFollowSystem = followSystem
    rebuildStyle()
  }, { immediate: true })

  return {
    applyCustomFont,
  }
}
